import React from 'react';
import { motion } from 'framer-motion';
import { SectionLabel, SectionHeading } from './ui/UI';
import { Target, Sparkles, Cloud, Globe, Code, Building, Wrench, CheckCircle2, FileText, Users, Briefcase } from 'lucide-react';
const capabilities = [{
  icon: Target,
  label: 'Attack-path validation'
}, {
  icon: Sparkles,
  label: 'AI application security'
}, {
  icon: Cloud,
  label: 'Cloud and identity risk'
}, {
  icon: Globe,
  label: 'External exposure'
}, {
  icon: Code,
  label: 'Code and secrets review'
}, {
  icon: Building,
  label: 'Internal exposure'
}, {
  icon: Wrench,
  label: 'Remediation guidance'
}, {
  icon: CheckCircle2,
  label: 'Fix verification'
}, {
  icon: FileText,
  label: 'Evidence packs'
}, {
  icon: Users,
  label: 'Customer-ready reporting'
}, {
  icon: Briefcase,
  label: 'MSSP-ready workflows'
}];
const outputs = ['Here are the paths that matter.', 'Here is why they matter.', 'Here is how to fix them.', 'Here is how to prove they are gone.'];
export function Product() {
  return <section id="product" className="relative w-full py-32 border-t border-white/5" data-id="element-727">
      <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-728">
        <SectionLabel number="05" label="Product Overview" className="mb-8" data-id="element-729" />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16" data-id="element-730">
          <div className="lg:col-span-7" data-id="element-731">
            <SectionHeading size="xl" data-id="element-732">What Periscan does.</SectionHeading>
          </div>
          <div className="lg:col-span-5 lg:pt-4" data-id="element-733">
            <p className="text-lg text-white/60 leading-relaxed" data-id="element-734">
              Periscan continuously validates how a company could actually be
              compromised. The output is not a list of thousands of findings —
              it is a clear, evidence-backed answer.
            </p>
          </div>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-16" data-id="element-735">
          {capabilities.map((cap, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: i * 0.04
        }} className="group relative p-6 rounded-xl bg-[#0a0d12] border border-white/[0.06] hover:border-[#1a8fd4]/40 hover:bg-[#0a0d12]/80 transition-all" data-id="element-736">
              <div className="flex items-center justify-between mb-4" data-id="element-737">
                <div className="w-9 h-9 rounded-lg bg-[#1a8fd4]/10 border border-[#1a8fd4]/20 flex items-center justify-center group-hover:bg-[#1a8fd4]/20 transition-colors" data-id="element-738">
                  <cap.icon className="w-4 h-4 text-[#1a8fd4]" data-id="element-739" />
                </div>
                <span className="font-mono text-[10px] text-white/30" data-id="element-740">
                  {String(i + 1).padStart(2, '0')}
                </span>
              </div>
              <p className="text-sm font-medium text-white" data-id="element-741">{cap.label}</p>
            </motion.div>)}
        </div>

        {/* The output */}
        <div className="rounded-2xl bg-[#070a0e] border border-white/5 p-10 md:p-16" data-id="element-742">
          <p className="font-mono text-xs uppercase tracking-widest text-[#1a8fd4] mb-8" data-id="element-743">
            The Output
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6" data-id="element-744">
            {outputs.map((output, i) => <motion.div key={i} initial={{
            opacity: 0,
            x: -10
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: i * 0.1
          }} className="flex items-baseline gap-4" data-id="element-745">
                <span className="font-mono text-sm text-[#1a8fd4]/60" data-id="element-746">
                  /{i + 1}
                </span>
                <p className="text-2xl md:text-3xl font-medium text-white tracking-tight" data-id="element-747">
                  {output}
                </p>
              </motion.div>)}
          </div>
        </div>
      </div>
    </section>;
}